import React, { useState, useRef } from "react";
import { timeToSeconds } from "../..";
import { DEFAULT_TIMESTAMP, EMPTY_STRING } from "../../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import CustomTooltip from "../ToolTip/CustomTooltip";
import IconButton from "../IconButton/IconButton";
import { Icons } from "../../../assets/icons";

const TimestampSelector = ({
  videoDuration = 3600,
  onTimeSelect,
  initialTime = DEFAULT_TIMESTAMP,
  error,
  disabled = false,
  isShowTooltip = false,
}) => {
  const [selectedTimestamp, setSelectedTimestamp] = useState(initialTime);
  const [timestampError, setTimeStampError] = useState("");
  const [showTimePicker, setShowTimePicker] = useState(false);
  const timeInputRef = useRef(null);

  const secondsToTimeString = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const maxTimeString = secondsToTimeString(videoDuration);

  const validateTime = (timeString) => {
    const selectedSeconds = timeToSeconds(timeString);
    if (selectedSeconds > videoDuration) {
      setTimeStampError(`Time cannot exceed video duration (${maxTimeString})`);
      return false;
    }
    setTimeStampError("");
    return true;
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setSelectedTimestamp(newTime);
    const isValid = validateTime(newTime);
    if (isValid && onTimeSelect) {
      onTimeSelect(newTime);
    }
  };

  const handleTimeInputClick = (e) => {
    e.stopPropagation();

    if (disabled) return;

    setShowTimePicker(true);
  };

  const TimePicker = () => {
    const [tempTime, setTempTime] = useState(selectedTimestamp);
    const [minutes, seconds] = tempTime.split(":").map(Number);

    const updateTempTime = (field, value) => {
      const newMinutes = field === "minutes" ? value : minutes;
      const newSeconds = field === "seconds" ? value : seconds;

      const newTimeString = `${newMinutes
        .toString()
        .padStart(2, "0")}:${newSeconds.toString().padStart(2, "0")}`;
      setTempTime(newTimeString);
    };

    const confirmTime = (e) => {
      e.stopPropagation();
      setSelectedTimestamp(tempTime);
      const isValid = validateTime(tempTime);
      if (isValid && onTimeSelect) {
        onTimeSelect(tempTime);
      }
      setShowTimePicker(false);
    };

    const cancelTime = (e) => {
      e.stopPropagation();
      setTempTime(selectedTimestamp);
      setShowTimePicker(false);
    };

    return (
      <div className="time-picker-dropdown card shadow-lg">
        <div className="card-body">
          <div className="time-selectors d-flex justify-content-center align-items-center mb-3">
            {/* Minutes */}
            <div className="text-center w-100">
              <label className="form-label small text-muted">M</label>
              <select
                value={minutes}
                onChange={(e) =>
                  updateTempTime("minutes", parseInt(e.target.value))
                }
                className="form-select form-select-sm time-select"
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>

            {/* Seconds */}
            <div className="text-center w-100">
              <label className="form-label small text-muted">S</label>
              <select
                value={seconds}
                onChange={(e) =>
                  updateTempTime("seconds", parseInt(e.target.value))
                }
                className="form-select form-select-sm time-select"
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="d-flex gap-2 buttons">
            <button onClick={cancelTime} className="flex-fill cancel px-0 py-1">
              Cancel
            </button>
            <button
              onClick={confirmTime}
              className="flex-fill submit px-0 py-1"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div onClick={handleTimeInputClick}>
        <label htmlFor="" className="select-label">
          time stamp:{" "}
          {isShowTooltip && (
            <CustomTooltip
              id="tooltip2"
              title="if there is a specific time stamp you want to see this clip let us know, otherwise we’ll find the best place for it."
              arrow
              placement="right"
              className="ml-2"
            >
              <span tabIndex="0">
                <IconButton icon={<Icons.InfoIcon />} width="12px" height="12px" />
              </span>
            </CustomTooltip>
          )}
        </label>
        <div
          className={`timestamp-selector position-relative form-control top-1 ${
            timestampError ? "is-invalid" : EMPTY_STRING
          } ${disabled ? "is-disabled" : EMPTY_STRING}`}
        >
          <div className="d-flex">
            <input
              ref={timeInputRef}
              type="text"
              value={selectedTimestamp}
              onChange={handleTimeChange}
              placeholder="MM:SS"
              className={`time-input ${disabled ? "is-disabled" : EMPTY_STRING}`}
              readOnly
              disabled={disabled}
            />
            <span className="time-icon">
              <FontAwesomeIcon icon={faClock} />
            </span>
          </div>

          {timestampError && (
            <p className="invalid-field-error mb-0 mt-1">
              Timestamp is greater than video duration
            </p>
          )}

          {showTimePicker && <TimePicker />}

          {showTimePicker && (
            <div
              className="time-picker-overlay"
              onClick={(e) => {
                e.stopPropagation();
                setShowTimePicker(false);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default TimestampSelector;
